import React, { useCallback, useState } from 'react';
import { Button, List, message } from 'antd';
import { ServiceRequestData } from '@services/types/Relationship/MyCalls/types';
import useSwr from '@hooks/useSwr';
import api from '@services/api';
import { DownloadOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import * as S from './styles';

interface MessagesAttachmentsSendingProps {
  request: ServiceRequestData | undefined;
}

const MessagesAttachments = ({ request }: MessagesAttachmentsSendingProps) => {
  const { t } = useTranslation('service-requests');

  const [openModal, setOpenModal] = useState(false);
  const [attachmentBeingDownloaded, setAttachmentBeingDownloaded] =
    useState<number>();

  const toggleOpenModal = useCallback(
    () => setOpenModal(current => !current),
    [],
  );

  const { data: attachments } = useSwr<{ id: number; name: string }[]>(
    'service-proxy/service/request/fileDetails',
    { params: { objectId: request?.id } },
  );

  const handleDownloadAttachment = useCallback(
    async (fileId: number, fileName: string) => {
      setAttachmentBeingDownloaded(fileId);

      try {
        const { data } = await api.get(
          `/service/request/fileDownload?fileId=${fileId}&fileName=${fileName}`,
          {
            responseType: 'blob',
            headers: {
              Accept: 'application/octet-stream',
            },
          },
        );

        const url = window.URL.createObjectURL(data);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      } catch {
        message.error(t('msgs.errorDownloadingAttachment'));
      } finally {
        setAttachmentBeingDownloaded(undefined);
      }
    },
    [t],
  );

  if (!attachments || attachments?.length === 0) {
    return <></>;
  }

  return (
    <S.MessagesAttachmentsContainer>
      <Button icon={<UnorderedListOutlined />} onClick={toggleOpenModal}>
        {t('attachments')}
      </Button>
      <S.MessagesAttachmentsModal
        title={t('attachments')}
        open={openModal}
        width={400}
        onCancel={toggleOpenModal}
        footer={false}
        destroyOnClose
      >
        <List
          dataSource={attachments}
          renderItem={item => (
            <List.Item>
              {item.name}
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                loading={attachmentBeingDownloaded === item.id}
                onClick={() => handleDownloadAttachment(item.id, item.name)}
              />
            </List.Item>
          )}
        />
      </S.MessagesAttachmentsModal>
    </S.MessagesAttachmentsContainer>
  );
};

export default MessagesAttachments;
